import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import {
  AlertService,
  CampaignService,
  FunnelInputService,
  FunnelService,
  FunnelStepService,
  LoaderService,
} from '../../../_services';
import {
  Breadcrumb,
  Campaign,
  Funnel,
  FunnelInputTypeEnum,
  FunnelStep,
  OfferIntents,
  OfferStages,
  ResolveOfferIntents,
} from '../../../_models';
import { takeUntil } from 'rxjs/operators';
import { Form } from '../../../_forms';
import { Location } from '@angular/common';
import {
  capitalizeFirstLetter,
  getOfferBuilderRoot,
  OfferBuilderSection,
  ReturnOfferIntents,
  TroubleshooterOfferIntents,
} from '../../../steps-library/step-util';

@Component({
  selector: 'app-stage-settings',
  templateUrl: './stage-settings.component.html',
  styleUrls: ['./stage-settings.component.scss'],
})
export class StageSettingsComponent extends Form implements OnInit {
  campaign: Campaign;
  offerId: number | string;
  offer: Funnel;
  offerIntent: number | string;
  confirmationStep: FunnelStep;
  responseStep: FunnelStep;
  confirmationPreviewLoading: boolean = false;
  responsePreviewLoading: boolean = false;
  passedResponseStep: FunnelStep;
  passedResponsePreviewLoading: boolean = false;
  bannerTitle: string;
  bannerSubtitle: string;
  isPublishing: boolean = false;
  currentSection: OfferBuilderSection = OfferBuilderSection.Retention;
  breadcrumbs: Breadcrumb[] = [];
  bannerMap = {
    [OfferIntents.CancelSubscription]: {
      title: `${OfferStages[OfferIntents.CancelSubscription]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer with an active subscription declines all save offers. This defines what they see in the last step of the cancellation flow.',
    },
    [OfferIntents.CancelTrial]: {
      title: `${OfferStages[OfferIntents.CancelTrial]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer in their trial period declines all save offers. This defines what they see in the last step of the trial-cancellation flow. ',
    },
    [OfferIntents.CancelSubscriptionHold]: {
      title: `${OfferStages[OfferIntents.CancelSubscriptionHold]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message for customers who just placed a new subscription order and decline all save offers. This defines the last step of the cancellation flow for new subscription purchases.',
    },
    [OfferIntents.CancelRebillHold]: {
      title: `${OfferStages[OfferIntents.CancelRebillHold]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message for a subscription rebill order and decline all save offers. This defines the last step of the cancellation flow for subscription rebills.',
    },
    [OfferIntents.CancelOrder]: {
      title: `${OfferStages[OfferIntents.CancelOrder]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer cancels a one-time order after rejecting all save offers. This determines what they see once the order cancellation is completed.',
    },
    [OfferIntents.CancelTrialHold]: {
      title: `${OfferStages[OfferIntents.CancelTrialHold]} — Cancellation Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer cancels a newly placed trial order after rejecting all save offers. This defines what they see at the end of the trial-order cancellation flow.',
    },
    [OfferIntents.ReturnOrder]: {
      title: `${OfferStages[OfferIntents.ReturnOrder]} — Return Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer returns a one-time order after rejecting all save offers. This determines what they see once the return is completed.',
    },
    [OfferIntents.ReturnSubscription]: {
      title: `${OfferStages[OfferIntents.ReturnSubscription]} — Return Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer with an active subscription requests a return after declining all save offers. This defines what they see in the last step of the return flow.',
    },
    [OfferIntents.ReturnTrial]: {
      title: `${OfferStages[OfferIntents.ReturnTrial]} — Return Settings`,
      subtitle:
        'Manage the final pop-up and confirmation message shown when a customer in their trial period requests a return after declining all save offers. This defines what they see in the last step of the trial-return flow.',
    },
    [OfferIntents.Resolve]: {
      title: `${OfferStages[OfferIntents.Resolve]} — Resolve Settings`,
      subtitle:
        'Manage the confirmation pop-up and response shown when a disputing customer requests a refund.',
    },
    [OfferIntents.ResolveSubscription]: {
      title: `${OfferStages[OfferIntents.ResolveSubscription]} — Resolve Settings`,
      subtitle:
        'Manage the confirmation pop-up and response shown when a disputing subscriber requests a refund and cancel.',
    },
  };
  stepLabelsMap = {
    cancel: {
      popupTitle: 'Cancellation Pop-up',
      popupDescription: 'Show a confirmation pop-up before executing the cancel action.',
      responseTitle: 'Cancellation Response',
      responseDescription:
        'Customize the final message your customers see after canceling. This confirms the cancellation and lets you ask for feedback or offer a way to easily resubscribe.',
    },
    return: {
      popupTitle: 'Return Pop-up',
      popupDescription: 'Show a confirmation pop-up before executing the return action.',
      responseTitle: 'Return Response',
      responseDescription:
        'Customize the final message your customers see after their return is confirmed. This confirms the return and lets you ask for feedback or share next steps.',
    },
    resolve: {
      popupTitle: 'Refund Pop-up',
      popupDescription: 'Show a confirmation pop-up before issuing the refund.',
      responseTitle: 'Refund Response',
      responseDescription: 'Customize the final message customers see after their refund is issued.',
    },
  };

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    protected funnelService: FunnelService,
    protected funnelStepService: FunnelStepService,
    protected funnelInputService: FunnelInputService,
    protected campaignService: CampaignService,
    protected loader: LoaderService,
    protected location: Location,
    protected alertService: AlertService
  ) {
    super(alertService, router, location);
    this.route.params.subscribe((params) => {
      this.offerId = params['offer_id'];
      this.offerIntent = params['offer_intent'];
    });
  }

  ngOnInit() {
    this.fetchOffer();
    this.funnelStepService.setFunnelId(this.offerId);
    this.campaign = this.campaignService.getCurrentCampaign();
    this.currentSection = this.route.snapshot.data['section'];

    if (this.offerIntent && this.bannerMap[this.offerIntent]) {
      this.bannerTitle = this.bannerMap[this.offerIntent].title;
      this.bannerSubtitle = this.bannerMap[this.offerIntent].subtitle;
    }
  }

  fetchOffer(): void {
    this.loader.show();
    this.funnelService.get(this.offerId).subscribe(
      (offer: Funnel) => {
        this.offer = offer;
        this.generateBreadcrumbs();
        this.applySegmentBanner();
        if (offer && offer.steps && offer.steps.length > 0) {
          // Find confirmation step (category: 28, type: 28)
          this.confirmationStep = offer.steps.find(
            (step) =>
              step.category === FunnelInputTypeEnum.Confirmation && step.type === FunnelInputTypeEnum.Confirmation
          );
          this.funnelInputService.setStepId(this.confirmationStep.id);

          // Find response step (category: 28, type: 10)
          this.responseStep = offer.steps.find(
            (step) =>
              step.category === FunnelInputTypeEnum.Confirmation && step.type === FunnelInputTypeEnum.InputActionStatus
          );

          // the backend resolves the troubleshooter Passed Response step (or null) authoritatively
          this.passedResponseStep = offer.troubleshooter_passed_response_step;
        }
        this.loader.hide();
      },
      (error) => {
        this.loader.hide();
        console.error('Error fetching offer:', error);
      }
    );
  }

  applySegmentBanner(): void {
    const intent = this.offer.offer_intent;
    const base = this.bannerMap[intent];

    if (base) {
      if (this.offer && this.offer.audience) {
        const segment = this.offer.audience_name;
        this.bannerTitle = `${base.title} (“${segment}” segment)`;
        this.bannerSubtitle =
          `${base.subtitle} These settings apply only to customers in the ‘${segment}’ segment ` +
          `and override the default stage settings.`;
      } else {
        this.bannerTitle = base.title;
        this.bannerSubtitle = base.subtitle;
      }
    }
  }

  generateBreadcrumbs() {
    const offerIntentLabel = this.offer ? OfferStages[this.offer.offer_intent] : null;

    this.breadcrumbs = [
      {
        label: capitalizeFirstLetter(this.currentSection),
        url: [getOfferBuilderRoot(this.currentSection)],
      },
      {
        label: `${offerIntentLabel} Stage Settings`,
      },
    ];
  }

  navigate(url: any[]) {
    this.router.navigate(url);
  }

  editConfirmation() {
    this.router.navigate([
      getOfferBuilderRoot(this.currentSection),
      'stage',
      this.offerId,
      'category',
      this.offer.offer_type,
      this.confirmationStep.category,
      'step',
      this.confirmationStep.id,
      this.confirmationStep.type,
    ]);
  }

  editResponse() {
    this.router.navigate([
      getOfferBuilderRoot(this.currentSection),
      'stage',
      this.offerId,
      'category',
      this.offer.offer_type,
      this.responseStep.category,
      'step',
      this.responseStep.id,
      this.responseStep.type,
    ]);
  }

  previewConfirmation() {
    this.confirmationPreviewLoading = true;
    const onSuccess = (data: any) => {
      window.open(data.toString(), '_blank');
      this.confirmationPreviewLoading = false;
    };

    const onError = (error: any) => {
      this.handleError(error);
      this.confirmationPreviewLoading = false;
    };

    this.funnelStepService.generatePreview(this.confirmationStep.id, this.campaign.id).subscribe(onSuccess, onError);
  }

  previewResponse() {
    this.responsePreviewLoading = true;
    const onSuccess = (data: any) => {
      window.open(data.toString(), '_blank');
      this.responsePreviewLoading = false;
    };

    const onError = (error: any) => {
      this.handleError(error);
      this.responsePreviewLoading = false;
    };

    this.funnelStepService.generatePreview(this.responseStep.id, this.campaign.id).subscribe(onSuccess, onError);
  }

  toggleStatus(): void {
    const input = this.confirmationStep.inputs[1];
    const payload = {
      enabled: !input.enabled,
    };

    this.funnelInputService.patch(input.id, payload).subscribe(
      () => {
        this.offer.is_modified = true;
        this.confirmationStep.inputs[1].enabled = payload.enabled;
        this.alertService.success('Step status updated successfully', true);
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  get isTroubleshooterIntent(): boolean {
    return !!this.offer && TroubleshooterOfferIntents.indexOf(this.offer.offer_intent) !== -1;
  }

  get isReturnIntent(): boolean {
    return !!this.offer && ReturnOfferIntents.indexOf(this.offer.offer_intent) !== -1;
  }

  get isResolveIntent(): boolean {
    return !!this.offer && ResolveOfferIntents.indexOf(this.offer.offer_intent) !== -1;
  }

  get stepLabels() {
    if (this.isResolveIntent) {
      return this.stepLabelsMap.resolve;
    }

    return this.isReturnIntent ? this.stepLabelsMap.return : this.stepLabelsMap.cancel;
  }

  editPassedResponse() {
    this.router.navigate([
      getOfferBuilderRoot(this.currentSection),
      'stage',
      this.offerId,
      'category',
      this.offer.offer_type,
      this.passedResponseStep.category,
      'step',
      this.passedResponseStep.id,
      this.passedResponseStep.type,
    ]);
  }

  previewPassedResponse() {
    this.passedResponsePreviewLoading = true;
    const onSuccess = (data: any) => {
      window.open(data.toString(), '_blank');
      this.passedResponsePreviewLoading = false;
    };

    const onError = (error: any) => {
      this.handleError(error);
      this.passedResponsePreviewLoading = false;
    };

    this.funnelStepService.generatePreview(this.passedResponseStep.id, this.campaign.id).subscribe(onSuccess, onError);
  }

  publish() {
    if (this.offer && this.offer.is_modified) {
      this.isPublishing = true;
      this.funnelService
        .publish(this.offer.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.offer.is_modified = false;
            this.alertService.success('Path changes are being published.', true);
            this.isPublishing = false;
          },
          (error) => {
            this.handleError(error);
            this.isPublishing = false;
          }
        );
    }
  }
}
